// VideoWelcomeScreen.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Video, 
  Mic, 
  Clock, 
  User, 
  FileText, 
  AlertCircle, 
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { verifyVideoTestLink } from 'api/apiHelper';

interface VideoTestInfo {
  valid: boolean;
  applicant_name: string;
  job_title: string;
  status: string;
  total_questions: number;
  current_question: number;
}

interface VideoWelcomeScreenProps {
  testToken?: string;
  onTestStarted?: () => void;
}

const VideoWelcomeScreen: React.FC<VideoWelcomeScreenProps> = ({ testToken: propTestToken, onTestStarted }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [testInfo, setTestInfo] = useState<VideoTestInfo | null>(null);
  const [permissionChecked, setPermissionChecked] = useState<boolean>(false);
  const [hasPermission, setHasPermission] = useState<boolean>(false);

  useEffect(() => {
    if (testToken) {
      verifyTest();
    }
  }, [testToken]);

  const verifyTest = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await verifyVideoTestLink(testToken!);
      console.log('Video test verification:', response.data);
      
      if (response.data.valid) {
        setTestInfo(response.data);
      } else {
        setError(response.data.message || 'Invalid test link');
        toast.error(response.data.message || 'Invalid test link');
      }
    } catch (error: any) {
      console.error('Video test verification error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify test link';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      setPermissionChecked(true);
      toast.success('Camera and microphone access granted!');
    } catch (err: any) {
      setHasPermission(false);
      setPermissionChecked(true);
      if (err.name === 'NotAllowedError') {
        toast.error('Please allow camera and microphone access to proceed');
      } else if (err.name === 'NotFoundError') {
        toast.error('No camera or microphone found');
      } else {
        toast.error(`Error accessing devices: ${err.message}`);
      }
    }
  };

  const handleStartTest = async () => {
    if (!hasPermission) {
      toast.warning('Please check device permissions first');
      return;
    }
    // In unified flow, start the test and trigger progression check
    if (window.location.pathname.includes('/unified-test/')) {
      try {
        const { startVideoTest } = await import('api/apiHelper');
        await startVideoTest(testToken!);
        toast.success('Video test started! Loading...');
        // Trigger progression check in UnifiedTestFlow
        if (onTestStarted) {
          setTimeout(() => {
            onTestStarted();
          }, 500);
        }
      } catch (error: any) {
        toast.error('Failed to start video test. Please try again.');
      }
    } else {
      navigate(`/video-test/${testToken}`);
    }
  };

  if (loading) {
    return <Backdrop message="Verifying test link..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Invalid Test Link</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-center text-gray-500">
            Please check your email for the correct link or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-4">
            <Video className="w-10 h-10 text-brand-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Video Assessment
          </h1>
          <p className="text-gray-600">Record your video responses</p>
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <User className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Applicant</p>
                <p className="font-semibold text-gray-800">{testInfo?.applicant_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Position</p>
                <p className="font-semibold text-gray-800">{testInfo?.job_title}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Test Instructions:</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">1</span>
              <span>This test contains <strong>{testInfo?.total_questions || 3} video questions</strong></span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">2</span>
              <span>You have <strong>2 minutes</strong> to record each response</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">3</span>
              <span>Recording will <strong>automatically stop</strong> when time runs out</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">4</span>
              <span>Videos are uploaded <strong>immediately</strong> after recording</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">5</span>
              <span>Ensure you have <strong>good lighting</strong> and a quiet environment</span>
            </li>
          </ul>
        </div>

        {/* Permission Check */}
        <div className={`rounded-lg p-4 mb-6 border-l-4 ${
          permissionChecked 
            ? hasPermission 
              ? 'bg-green-50 border-green-400' 
              : 'bg-red-50 border-red-400'
            : 'bg-yellow-50 border-yellow-400'
        }`}>
          <div className="flex">
            {permissionChecked ? (
              hasPermission ? (
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              )
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${
                permissionChecked 
                  ? hasPermission ? 'text-green-800' : 'text-red-800'
                  : 'text-yellow-800'
              }`}>
                {permissionChecked 
                  ? hasPermission 
                    ? 'Camera & Microphone Ready!' 
                    : 'Permission Denied'
                  : 'Permission Check Required'
                }
              </p>
              <p className={`text-sm mt-1 ${
                permissionChecked 
                  ? hasPermission ? 'text-green-700' : 'text-red-700'
                  : 'text-yellow-700'
              }`}>
                {permissionChecked
                  ? hasPermission
                    ? 'Your devices are ready. You can now start the test.'
                    : 'Please allow camera and microphone access in your browser settings.'
                  : 'Click the button below to grant camera and microphone access.'
                }
              </p>
              {!permissionChecked && (
                <button
                  onClick={checkPermissions}
                  className="mt-3 px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-lg text-sm font-medium transition-all flex items-center"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Check Permissions
                </button>
              )}
              {permissionChecked && !hasPermission && (
                <button
                  onClick={checkPermissions}
                  className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition-all"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleStartTest}
          disabled={!hasPermission}
          className={`w-full font-semibold py-4 px-6 rounded-lg transition duration-200 flex items-center justify-center ${
            hasPermission
              ? 'bg-brand-500 hover:bg-brand-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Clock className="w-5 h-5 mr-2" />
          Start Video Test
        </button>

        <p className="text-center text-sm text-gray-500 mt-4">
          By starting the test, you agree to complete it in one sitting.
        </p>
      </div>
    </div>
  );
};

export default VideoWelcomeScreen;
